import React, { ReactNode } from "react";

import Popconfirm from "antd/es/popconfirm";
import Tooltip from "antd/es/tooltip";
import camelCase from "lodash-es/camelCase";
import { useIntl } from "react-intl";

import { AnyEntity, EntityType } from "@mapmycustomers/shared/types/entity";
import { Alert, Button } from "@mapmycustomers/ui";

import styles from "./DeleteButton.module.scss";

interface Props {
  containsRestrictedDeleteItems?: boolean;
  deleteConfirmationVisible: boolean;
  deleting: boolean;
  disabled?: boolean;
  entityType?: EntityType;
  onDelete: () => void;
  onHideConfirmation: () => void;
  onShowConfirmation: () => void;
  selectedEntityIds: Array<AnyEntity["id"]>;
  selectedEntityTypes?: Array<EntityType>;
  tooltip?: ReactNode;
}

const DeleteButton: React.FC<Props> = ({
  containsRestrictedDeleteItems,
  deleteConfirmationVisible,
  deleting,
  disabled,
  entityType,
  onDelete,
  onHideConfirmation,
  onShowConfirmation,
  selectedEntityIds,
  selectedEntityTypes,
  tooltip,
}) => {
  const intl = useIntl();

  const entityTypes = entityType ? [entityType] : selectedEntityTypes ?? [];

  const entityNameAccusative =
    entityTypes.length === 1
      ? intl.formatMessage(
          {
            id: "view.selectBar.records.one",
            defaultMessage:
              "{count, plural, one {{entityType, select, accounts {Company} contacts {Person} deals {Deal} crmActivities {Activity} routes {Route} pins {Pin} territories {Territory} trips {Drive} leads {Lead} other {Record}}} other {{entityType, select, accounts {Companies} contacts {People} deals {Deals} crmActivities {Activities} routes {Routes} pins {Pins} territories {Territories} trips {Drives} leads {Leads} other {Records}}}}",
          },
          {
            count: selectedEntityIds.length,
            entityType: camelCase(entityTypes[0]),
          }
        )
      : intl.formatMessage(
          {
            id: "view.selectBar.records",
            defaultMessage: "{count, plural, one {Record} other {Records}}",
          },
          {
            count: selectedEntityIds.length,
          }
        );

  return (
    <Popconfirm
      className={styles.popconfirm}
      okButtonProps={{
        danger: true,
        loading: deleting,
        type: "default",
      }}
      okText={intl.formatMessage({
        id: "view.selectBar.button.delete.popConfirm.ok",
        defaultMessage: "Delete",
        description: "Delete confirmation",
      })}
      onCancel={onHideConfirmation}
      onConfirm={onDelete}
      open={deleteConfirmationVisible}
      overlayClassName={styles.confirmOverlay}
      placement="right"
      title={
        <div className={styles.popconfirmContainer}>
          {containsRestrictedDeleteItems && (
            <Alert
              className={styles.containsRestrictedDeleteItems}
              message={intl.formatMessage(
                {
                  id: "view.selectBar.button.delete.containsRestrictedDeleteItems",
                  defaultMessage:
                    "Please note, <b>one or more record in this selection cannot be deleted</b> as you don’t have permission to do so.",
                  description: "Contains Restricted Delete Items info",
                },
                {
                  b: (text) => <b>{text}</b>,
                }
              )}
              showIcon
              type="warning"
            />
          )}
          <div className={styles.text}>
            {intl.formatMessage(
              {
                id: "view.selectBar.button.delete.popConfirm.text",
                defaultMessage:
                  "<b>Are you sure?</b> You have the option to restore deleted records for up to 90 days.",
                description: "Delete confirmation",
              },
              { b: (text) => <b>{text}</b> }
            )}
          </div>
        </div>
      }
    >
      <Tooltip title={tooltip}>
        <Button danger disabled={disabled} onClick={onShowConfirmation}>
          {intl.formatMessage(
            {
              id: "view.selectBar.button.delete",
              defaultMessage: "Delete {entityName}",
              description: "Delete button on a selected entities bar",
            },
            {
              entityName: entityNameAccusative,
            }
          )}
        </Button>
      </Tooltip>
    </Popconfirm>
  );
};

export default DeleteButton;
